import { RootState, AppThunk } from "ducks/state";
import { createSelector } from "reselect";
import { newNotification } from "./notification";
import { hen, Hen } from "@udok/lib/internal/store";
import { MarketplaceRecipient } from "@udok/lib/api/models";
import {
  createRecipient,
  fetchRecipients,
  fetchRecipient,
  deleteRecipient,
} from "@udok/lib/api/marketplace";
import { getToken, UNAUTHORIZED } from "./auth";

export type InitialState = {
  recipientByID: { [mareID: string]: MarketplaceRecipient | undefined };
};

// Reducers
const initialState: InitialState = {
  recipientByID: {},
};

class MarketplaceRecipientSlice extends Hen<InitialState> {
  recipientsLoaded(notes: MarketplaceRecipient[]) {
    notes.forEach((note) => {
      this.state.recipientByID[note.mareID] = note;
    });
  }

  recipientRemoved(mareID: string) {
    this.state.recipientByID[mareID] = undefined;
  }
}

export const [Reducer, actions] = hen(
  new MarketplaceRecipientSlice(initialState),
  {
    [UNAUTHORIZED]: () => {
      return initialState;
    },
  }
);

// Selectors
const mainSelector = (state: RootState) => state.marketplace;

export const recipientsView = createSelector(mainSelector, (state) => {
  return {
    list: Object.keys(state.recipientByID)
      .map((mareID) => state.recipientByID[mareID])
      .filter((a) => a !== undefined) as MarketplaceRecipient[],
  };
});

export const getRecipient = (state: RootState, props: { mareID: string }) =>
  createSelector(mainSelector, (state) => {
    return {
      note: state.recipientByID[props.mareID],
    };
  });

// Actions
export function createOneMarketplaceRecipient(
  note: MarketplaceRecipient
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;

    return createRecipient(apiToken, note)
      .then((r) => {
        dispatch(actions.recipientsLoaded([r]));
        dispatch(
          newNotification("general", {
            status: "success",
            message: "Salvo com sucesso",
          })
        );
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function loadMarketplaceRecipients(): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;

    return fetchRecipients(apiToken)
      .then((r) => {
        dispatch(actions.recipientsLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function loadOneMarketplaceRecipient(
  mareID: string
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;

    return fetchRecipient(apiToken, mareID)
      .then((r) => {
        dispatch(actions.recipientsLoaded([r]));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function removeMarketplaceRecipient(
  mareID: string
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return deleteRecipient(apiToken, mareID)
      .then((r) => {
        dispatch(actions.recipientRemoved(r.mareID));
        dispatch(
          newNotification("general", {
            status: "success",
            message: "Removido com sucesso",
          })
        );
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}
